/**
 * The toolbar every admin list shares: a search box, the filters that make
 * sense for that list, a sort control and a Clear that appears while anything
 * is filtered.
 *
 * It holds no list state of its own beyond the text being typed. The page owns
 * the values (normally through `useListUrlState`, so they live in the address)
 * and hands them in, which keeps what the toolbar shows and what the page
 * queries from ever disagreeing.
 *
 * A control that works on the rows already loaded, rather than asking the
 * server, says so in a tooltip: filtering or sorting a partly loaded list can
 * hide a row that sits on a page not fetched yet, and an admin should know
 * that before trusting an empty result.
 */

import { useEffect, useRef, useState } from "react";
import {
  ArrowsDownUp,
  Info,
  MagnifyingGlass as Search,
} from "@phosphor-icons/react";
import { Tooltip } from "@charisol-spark/ui";
import { Button } from "@/components/ui/Button";
import { cn } from "@/utils/cn";
import type { ListSort } from "./useListUrlState";

export const PAGE_ONLY_NOTE =
  "Applies to the rows loaded so far. Load more to bring in the rest.";

const FIELD_CLASS =
  "block h-9 rounded-full border border-input bg-background px-3 text-sm placeholder:text-muted-foreground focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30";

export interface ToolbarFilter {
  /** The address parameter, also used for the control id. */
  id: string;
  label: string;
  kind: "select" | "date" | "text";
  value: string;
  onChange: (value: string) => void;
  options?: readonly { value: string; label: string }[];
  placeholder?: string;
  /** `page` marks a filter that only narrows the rows already loaded. */
  scope?: "server" | "page";
  min?: string;
  max?: string;
}

export interface ToolbarSortOption {
  field: string;
  label: string;
  /** How the two directions read; defaults to dates. */
  kind?: "date" | "text" | "number";
}

const DIRECTION_WORDS = {
  date: { asc: "oldest first", desc: "newest first" },
  text: { asc: "A to Z", desc: "Z to A" },
  number: { asc: "low to high", desc: "high to low" },
} as const;

export interface AdminListToolbarProps {
  /** Names the toolbar for assistive technology, e.g. "Users". */
  label: string;
  search?: {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    /** The visible and accessible name; defaults to "Search". */
    label?: string;
    /** Wait this long after the last keystroke before applying. */
    debounceMs?: number;
    scope?: "server" | "page";
  };
  filters?: readonly ToolbarFilter[];
  sort?: {
    options: readonly ToolbarSortOption[];
    value: ListSort;
    onChange: (sort: ListSort) => void;
    scope?: "server" | "page";
  };
  hasActive: boolean;
  onClear: () => void;
}

function PageOnlyHint() {
  return (
    <Tooltip content={PAGE_ONLY_NOTE} side="top">
      <span
        tabIndex={0}
        role="img"
        aria-label={PAGE_ONLY_NOTE}
        className="inline-flex text-muted-foreground"
      >
        <Info className="h-3.5 w-3.5" aria-hidden />
      </span>
    </Tooltip>
  );
}

/** A free-text filter: applied a moment after typing stops, not on every key. */
function TextFilterInput({ filter }: { filter: ToolbarFilter }) {
  const [text, setText] = useState(filter.value);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => setText(filter.value), [filter.value]);
  useEffect(() => () => clearTimeout(timer.current), []);
  return (
    <input
      id={`list-filter-${filter.id}`}
      type="text"
      value={text}
      placeholder={filter.placeholder}
      onChange={(e) => {
        const next = e.target.value;
        setText(next);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => filter.onChange(next), 400);
      }}
      className={FIELD_CLASS}
    />
  );
}

function SearchBox({
  search,
}: {
  search: NonNullable<AdminListToolbarProps["search"]>;
}) {
  const { value, onChange, debounceMs = 350 } = search;
  const [text, setText] = useState(value);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // The address can change under the box (Clear, Back, a pasted link).
  useEffect(() => setText(value), [value]);
  useEffect(() => () => clearTimeout(timer.current), []);

  const label = search.label ?? "Search";
  return (
    <div className="min-w-[14rem] max-w-md flex-1 space-y-1">
      <label
        htmlFor="list-search"
        className="flex items-center gap-1 text-xs font-medium text-muted-foreground"
      >
        {label}
        {search.scope === "page" && <PageOnlyHint />}
      </label>
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <input
          id="list-search"
          type="search"
          value={text}
          onChange={(e) => {
            const next = e.target.value;
            setText(next);
            clearTimeout(timer.current);
            timer.current = setTimeout(() => onChange(next), debounceMs);
          }}
          placeholder={search.placeholder}
          className={cn(FIELD_CLASS, "w-full pl-9 pr-4")}
        />
      </div>
    </div>
  );
}

export function AdminListToolbar({
  label,
  search,
  filters = [],
  sort,
  hasActive,
  onClear,
}: AdminListToolbarProps) {
  const sortChoices = sort
    ? sort.options.flatMap((option) =>
        (["desc", "asc"] as const).map((dir) => ({
          key: `${option.field}:${dir}`,
          field: option.field,
          dir,
          label: `${option.label}, ${DIRECTION_WORDS[option.kind ?? "date"][dir]}`,
        }))
      )
    : [];

  return (
    <div
      role="search"
      aria-label={`${label} filters`}
      className="flex flex-wrap items-end gap-3"
    >
      {search && <SearchBox search={search} />}

      {filters.map((filter) => (
        <div key={filter.id} className="space-y-1">
          <label
            htmlFor={`list-filter-${filter.id}`}
            className="flex items-center gap-1 text-xs font-medium text-muted-foreground"
          >
            {filter.label}
            {filter.scope === "page" && <PageOnlyHint />}
          </label>
          {filter.kind === "select" ? (
            <select
              id={`list-filter-${filter.id}`}
              value={filter.value}
              onChange={(e) => filter.onChange(e.target.value)}
              className={FIELD_CLASS}
            >
              {filter.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          ) : filter.kind === "text" ? (
            <TextFilterInput filter={filter} />
          ) : (
            <input
              id={`list-filter-${filter.id}`}
              type="date"
              value={filter.value}
              min={filter.min}
              max={filter.max}
              onChange={(e) => filter.onChange(e.target.value)}
              className={FIELD_CLASS}
            />
          )}
        </div>
      ))}

      {sort && (
        <div className="space-y-1">
          <label
            htmlFor="list-sort"
            className="flex items-center gap-1 text-xs font-medium text-muted-foreground"
          >
            <ArrowsDownUp className="h-3 w-3" aria-hidden />
            Sort
            {sort.scope !== "server" && <PageOnlyHint />}
          </label>
          <select
            id="list-sort"
            value={`${sort.value.field}:${sort.value.dir}`}
            onChange={(e) => {
              const choice = sortChoices.find((c) => c.key === e.target.value);
              if (choice) {
                sort.onChange({ field: choice.field, dir: choice.dir });
              }
            }}
            className={FIELD_CLASS}
          >
            {sortChoices.map((choice) => (
              <option key={choice.key} value={choice.key}>
                {choice.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {hasActive && (
        <Button variant="ghost" size="sm" pill onClick={onClear}>
          Clear filters
        </Button>
      )}
    </div>
  );
}
